import {
  Bell,
  Invoice,
  MoneyWavy,
  Warning,
  Wrench,
} from '@phosphor-icons/react';

import {
  CardDeclineMetadata,
  CashAdvanceConsentMetadata,
  MaintenanceReminderMetadata,
  ManualMileageRequestMetadata,
  MonthlyStatementMetadata,
  NotificationTypes,
  PreventativeMaintenanceReminderCreatedMetadata,
  ScheduledWithdrawalPartialMetadata,
  ScheduledWithdrawalPausedMetadata,
  ScheduledWithdrawalSkippedMetadata,
} from '../types/apiTypes';
import { Notification } from '../types/dbTypes';
import { formatDate, getMonthName } from './DateUtils';
import { formatNumberCommas } from './CurrencyUtils';

// The cash-advance "options" notification metadata type was removed from the backend's generated
// types; kept locally so this handler still renders + links existing notifications of that type.
interface CashAdvanceOptionsMetadata {
  kind?: 'reminder' | 'expired' | 'initial';
  request_id?: string;
}

// Written by the backend's deposit-failure activity (depositActivities.ts).
interface DepositFailedMetadata {
  dollar_amount?: string;
  failure_code?: string;
  reason?: string;
}

const getMaintenanceLabel = (maintenanceType?: string) => {
  switch (maintenanceType) {
    case 'preventative':
      return 'preventive maintenance';
    case 'repairs':
      return 'repairs';
    case 'tires':
      return 'tire service';
    default:
      return 'maintenance';
  }
};

const getScheduledWithdrawalName = (label?: string | null) =>
  label ? `Your "${label}" scheduled withdrawal` : 'Your scheduled withdrawal';

// The backend will save the sentence it builds for these types as `metadata.message`, so every
// surface (push, web, mobile) says the same thing. Until it does, and for rows saved before it
// did, the cases below build the sentence from the other metadata fields.
const BACKEND_MESSAGE_TYPES: string[] = [
  NotificationTypes.depositFailed,
  NotificationTypes.scheduledWithdrawalSkipped,
  NotificationTypes.scheduledWithdrawalPartial,
  NotificationTypes.scheduledWithdrawalPaused,
];

const getBackendMessage = (notification: Notification) => {
  const type = notification.notification_type;
  if (!type || !BACKEND_MESSAGE_TYPES.includes(type)) {
    return null;
  }
  const message = (notification.metadata as { message?: unknown } | null)
    ?.message;
  return typeof message === 'string' && message.trim() ? message : null;
};

export const renderNotificationMessage = (notification: Notification) => {
  const backendMessage = getBackendMessage(notification);
  if (backendMessage) return <p>{backendMessage}</p>;

  switch (notification.notification_type) {
    case NotificationTypes.maintenanceReminder:
      const maintenanceData =
        notification.metadata as MaintenanceReminderMetadata;
      const maintenanceLabel = getMaintenanceLabel(
        maintenanceData.maintenance_type
      );
      const maintenanceDue = maintenanceData.end_date
        ? ` by ${formatDate(new Date(maintenanceData.end_date))}`
        : maintenanceData.end_mileage != null
          ? ` by ${formatNumberCommas(maintenanceData.end_mileage)} miles`
          : '';
      return (
        <p>{`${maintenanceData.asset_display_name} is due for ${maintenanceLabel}${maintenanceDue}.`}</p>
      );

    case NotificationTypes.monthlyStatement:
      const statementData = notification.metadata as MonthlyStatementMetadata;
      return (
        <p>{`Your monthly statement for ${getMonthName(statementData.month)}, ${statementData.year} is ready.`}</p>
      );
    case NotificationTypes.manualMileageRequest:
      const mileageData = notification.metadata as ManualMileageRequestMetadata;
      return (
        <p>{`Please fill out your truck mileage report for this week (${mileageData.week_start_date} to ${mileageData.week_end_date}).`}</p>
      );
    case NotificationTypes.preventativeMaintenanceReminderCreated:
      const preventativeMaintenanceData =
        notification.metadata as PreventativeMaintenanceReminderCreatedMetadata;
      return (
        <p>{`We detected a PM expense, so we created a new PM reminder for your truck ${preventativeMaintenanceData.truck_display_name}.`}</p>
      );
    case NotificationTypes.cardDecline:
      const declineData = notification.metadata as CardDeclineMetadata;
      const endingDigits = declineData.last_four_digits
        ? ` ending in ${declineData.last_four_digits}`
        : '';
      return (
        <p>{`Your recent purchase attempt for ${declineData.dollar_amount} on your card${endingDigits} has been declined. Reason: ${declineData.decline_reason}.`}</p>
      );
    case NotificationTypes.cashAdvanceConsentRequired:
      return (
        <p>
          Your Cash Advance has been approved! Sign the agreement to receive
          your funds.
        </p>
      );
    case NotificationTypes.cashAdvanceOptions:
      const optionsData = notification.metadata as CashAdvanceOptionsMetadata;
      switch (optionsData?.kind) {
        case 'reminder':
          return (
            <p>
              Reminder: your repayment options are waiting. Review and choose
              before they expire.
            </p>
          );
        case 'expired':
          return (
            <p>
              Your repayment options have expired. Request a new cash advance or
              contact support.
            </p>
          );
        case 'initial':
        default:
          return (
            <p>
              Your repayment options are ready to review. Choose your preferred
              plan to continue.
            </p>
          );
      }
    case NotificationTypes.pendingTruckApproval:
      return (
        <p>
          A new truck from your ELD provider needs your approval. Review and
          confirm its details.
        </p>
      );

    case NotificationTypes.depositFailed:
      const depositData = notification.metadata as DepositFailedMetadata;
      const depositAmount = depositData?.dollar_amount
        ? ` ${depositData.dollar_amount}`
        : '';
      const depositReason = depositData?.reason ? ` ${depositData.reason}` : '';
      return (
        <p>{`Your${depositAmount} deposit couldn't be completed.${depositReason}`}</p>
      );
    case NotificationTypes.scheduledWithdrawalSkipped:
      const skippedData =
        notification.metadata as Partial<ScheduledWithdrawalSkippedMetadata>;
      const skippedAvailable = skippedData?.available_amount
        ? `only ${skippedData.available_amount} was available`
        : 'not enough was available';
      const skippedNext = skippedData?.next_run_date
        ? ` Next attempt: ${formatDate(new Date(skippedData.next_run_date), true)}.`
        : '';
      return (
        <p>{`${getScheduledWithdrawalName(skippedData?.label)} didn't run — ${skippedAvailable}. Nothing was withdrawn.${skippedNext}`}</p>
      );
    case NotificationTypes.scheduledWithdrawalPartial:
      const partialData =
        notification.metadata as Partial<ScheduledWithdrawalPartialMetadata>;
      const partialSent = partialData?.withdrawn_amount
        ? `sent ${partialData.withdrawn_amount}`
        : 'sent less than scheduled';
      const partialWhy = partialData?.withdrawn_amount
        ? ' — less than scheduled, because that was what was available'
        : ', because that was what was available';
      return (
        <p>{`${getScheduledWithdrawalName(partialData?.label)} ${partialSent}${partialWhy}.`}</p>
      );
    case NotificationTypes.scheduledWithdrawalPaused:
      const pausedData =
        notification.metadata as Partial<ScheduledWithdrawalPausedMetadata>;
      const pausedWhy = pausedData?.reason_description
        ? ` because ${pausedData.reason_description}`
        : '';
      const pausedRecovery = pausedData?.recovery_instruction
        ? ` ${pausedData.recovery_instruction}`
        : '';
      return (
        <p>{`${getScheduledWithdrawalName(pausedData?.label)} was paused${pausedWhy}.${pausedRecovery}`}</p>
      );

    default:
      return <p>You have a new notification.</p>;
  }
};

export const getNotificationLink = (notification: Notification) => {
  switch (notification.notification_type) {
    case NotificationTypes.maintenanceReminder:
      const maintenanceData =
        notification.metadata as MaintenanceReminderMetadata;
      return `/dashboard/assets/${maintenanceData.asset_type}/${maintenanceData.asset_id}/maintenance`;
    case NotificationTypes.monthlyStatement:
      return `/dashboard/banking/statements`;
    case NotificationTypes.cardDecline:
    case NotificationTypes.depositFailed:
    case NotificationTypes.scheduledWithdrawalSkipped:
    case NotificationTypes.scheduledWithdrawalPartial:
    case NotificationTypes.scheduledWithdrawalPaused:
      return `/dashboard/banking/overview`;
    case NotificationTypes.manualMileageRequest:
      return `/dashboard/assets/submit-mileage`;
    case NotificationTypes.preventativeMaintenanceReminderCreated:
      const preventativeMaintenanceData =
        notification.metadata as PreventativeMaintenanceReminderCreatedMetadata;
      return `/dashboard/assets/truck/${preventativeMaintenanceData.truck_id}/maintenance`;
    case NotificationTypes.cashAdvanceConsentRequired:
      const consentData = notification.metadata as CashAdvanceConsentMetadata;
      return `/dashboard/banking/cash-advance/consent/${consentData.loan_request_id}`;
    case NotificationTypes.cashAdvanceOptions:
      const optionsLinkData =
        notification.metadata as CashAdvanceOptionsMetadata;
      // Guard the optional request_id — interpolating undefined would yield a dead ".../undefined" route.
      return optionsLinkData.request_id
        ? `/dashboard/banking/cash-advance/options/${optionsLinkData.request_id}`
        : null;
    case NotificationTypes.pendingTruckApproval:
      return `/dashboard/assets`;
    default:
      return null;
  }
};

export const renderNotificationIcon = (notification: Notification) => {
  switch (notification.notification_type) {
    case NotificationTypes.maintenanceReminder:
      return (
        <div className="flex h-11 w-11 min-w-11 items-center justify-center rounded-[4px] border-[1px] border-bordergray bg-[#F5F6F7]">
          <Wrench size={24} className="text-graysecondary" />
        </div>
      );
    case NotificationTypes.monthlyStatement:
      return (
        <div className="flex h-11 w-11 min-w-11 items-center justify-center rounded-[4px] border-[1px] border-bordergray bg-[#F5F6F7]">
          <Invoice size={24} className="text-graysecondary" />
        </div>
      );
    case NotificationTypes.cardDecline:
    case NotificationTypes.depositFailed:
      return (
        <div className="flex h-11 w-11 min-w-11 items-center justify-center rounded-full bg-orange-200">
          <Warning size={24} className="text-orange-500" />
        </div>
      );
    case NotificationTypes.manualMileageRequest:
      return (
        <div className="flex h-11 w-11 min-w-11 items-center justify-center rounded-[4px] border-[1px] border-bordergray bg-[#F5F6F7]">
          <img
            src="/truck.svg"
            className="h-6 w-6 text-graysecondary"
            alt="Truck"
          />
        </div>
      );
    case NotificationTypes.cashAdvanceConsentRequired:
    case NotificationTypes.cashAdvanceOptions:
    case NotificationTypes.scheduledWithdrawalSkipped:
    case NotificationTypes.scheduledWithdrawalPartial:
    case NotificationTypes.scheduledWithdrawalPaused:
      return (
        <div className="flex h-11 w-11 min-w-11 items-center justify-center rounded-[4px] border-[1px] border-bordergray bg-[#F5F6F7]">
          <MoneyWavy size={24} className="text-primary" />
        </div>
      );
    case NotificationTypes.pendingTruckApproval:
      return (
        <div className="flex h-11 w-11 min-w-11 items-center justify-center rounded-[4px] border-[1px] border-bordergray bg-yellow-50">
          <img
            src="/truck.svg"
            className="h-6 w-6 text-graysecondary"
            alt="Truck"
          />
        </div>
      );
    default:
      return (
        <div className="flex h-11 w-11 min-w-11 items-center justify-center rounded-[4px] border-[1px] border-bordergray bg-[#F5F6F7]">
          <Bell size={24} className="text-graysecondary" />
        </div>
      );
  }
};
